import { CalendarApi, EventApi, EventInput } from '@fullcalendar/core/index.js';
import { EventImpl } from '@fullcalendar/core/internal';
import FullCalendar from '@fullcalendar/react';
import { StoreOrderingForm } from 'api/ordering-service/ordering-service.types';
import i18n from 'app-i18n';
import { DateTime } from 'luxon';
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DUMMY_EVENT_ID,
  LocationHoursOrderType,
  SetHoursFormDataProps,
  createDummyEvent,
} from '../location-hours.utils';
import { convertNormalHoursToEventInput } from '../normal-hours-calendar/normal-hours.utils';
import {
  convertClosedDatesToEventInput,
  convertSetClosedDatesFormDataToEventSource,
  eventsToClosedDates,
  filterClosedDatesOverlappedEvents,
} from './closed-dates.utils';
import {
  convertSetTemporaryHoursFormDataToEventSource,
  convertTemporaryHoursToEventInput,
  eventsToTemporaryHours,
  filterTemporaryHoursOverlappedEvents,
  generateBackgroundEvents,
} from './temporary-hours.utils';

export type TemporaryHoursType = {
  temporaryHoursRef: MutableRefObject<FullCalendar>;
  temporaryEvents: EventInput[];
  backgroundEvents: EventInput[];
  calendarView: 'list' | 'week' | 'month' | 'year';
  activeStart: Date;
  activeEnd: Date;
  addDummyEvent: (
    start: DateTime,
    end: DateTime,
    orderType: LocationHoursOrderType,
  ) => EventInput;
  removeDummyEvent: () => void;
  addEvents: (setHourFormData: SetHoursFormDataProps) => void;
  eventsSet: (events: EventApi[]) => void;
  handleCalendarDateChange: (
    calendarApi: CalendarApi,
    direction: 'prev' | 'today' | 'next',
  ) => void;
  handleCalendarViewChange: (
    calendarApi: CalendarApi,
    view: 'list' | 'week' | 'month' | 'year',
  ) => void;
  changeToWeekOfDateView: (
    calendarApi: CalendarApi,
    dateSelect: DateTime,
  ) => void;
  convertEventsToTemporaryHours: () => Partial<StoreOrderingForm>;
  temporaryHoursCalendarLoaded: boolean;
};

export type ClosedDatesType = {
  addDummyEvent: (
    start: DateTime,
    end: DateTime,
    oderType: LocationHoursOrderType,
  ) => EventInput;
  removeDummyEvent: () => void;
  addEvents: (setHourFormData: SetHoursFormDataProps) => void;
  convertEventsToClosedDates: () => Partial<StoreOrderingForm>;
};

export type SideCalendarType = {
  sideCalendarValue: DateTime;
  setSideCalendarValue: Dispatch<SetStateAction<DateTime>>;
};

export type StandardHoursType = {
  isShowingNormalHours: boolean;
  setIsShowingNormalHours: Dispatch<SetStateAction<boolean>>;
};

type UseTemporaryHoursProps = {
  storeOrderingData: StoreOrderingForm;
  normalEvents?: EventInput[];
};

// Define custom hook for handling temporary hours
export const useTemporaryHours = ({
  storeOrderingData,
  normalEvents,
}: UseTemporaryHoursProps) => {
  const {
    pickupOrderHours,
    deliveryOrderHours,
    pickupTemporaryHours,
    deliveryTemporaryHours,
    pickupOffDays,
    deliveryOffDays,
  } = storeOrderingData;

  const temporaryHoursRef = useRef<FullCalendar>();
  const temporaryHoursCalendarApi = temporaryHoursRef.current?.getApi();
  const [temporaryEvents, setTemporaryEvents] = useState<EventInput[]>([]);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [calendarView, setCalendarView] = useState<
    'list' | 'week' | 'month' | 'year'
  >('week');
  const activeStart = useMemo(
    () => temporaryHoursCalendarApi?.view.activeStart,
    [temporaryHoursCalendarApi?.view.activeStart],
  );

  const activeEnd = useMemo(
    () => temporaryHoursCalendarApi?.view.activeEnd,
    [temporaryHoursCalendarApi?.view.activeEnd],
  );

  const [sideCalendarValue, setSideCalendarValue] = useState<DateTime | null>(
    DateTime.local(),
  );

  const [isShowingNormalHours, setIsShowingNormalHours] =
    useState<boolean>(false);

  const backgroundEvents = useMemo(() => {
    const normalCalendarEvents =
      normalEvents !== undefined
        ? normalEvents
        : [
            ...convertNormalHoursToEventInput(pickupOrderHours, {
              orderType: 'PICKUP',
              eventType: 'LocationHours',
            }),
            ...convertNormalHoursToEventInput(deliveryOrderHours, {
              orderType: 'DELIVERY',
              eventType: 'LocationHours',
            }),
          ];

    return generateBackgroundEvents(normalCalendarEvents, isShowingNormalHours);
  }, [
    normalEvents,
    pickupOrderHours,
    deliveryOrderHours,
    isShowingNormalHours,
  ]);

  const initialEvents = useMemo(
    () => [
      ...convertTemporaryHoursToEventInput(pickupTemporaryHours, ['PICKUP']),
      ...convertTemporaryHoursToEventInput(deliveryTemporaryHours, [
        'DELIVERY',
      ]),
      ...convertClosedDatesToEventInput(pickupOffDays, 'PICKUP_CLOSED'),
      ...convertClosedDatesToEventInput(deliveryOffDays, 'DELIVERY_CLOSED'),
    ],
    [
      deliveryOffDays,
      deliveryTemporaryHours,
      pickupOffDays,
      pickupTemporaryHours,
    ],
  );

  const addTemporaryDummyEvent = useCallback(
    (start: DateTime, end: DateTime, orderType: LocationHoursOrderType) => {
      const title =
        orderType === 'PICKUP'
          ? i18n.t('LocationHours.eventTitles.pickup')
          : i18n.t('LocationHours.eventTitles.delivery');

      const dummyEvent = createDummyEvent(start, end, title, orderType);
      temporaryHoursCalendarApi?.addEvent(dummyEvent);
      return dummyEvent;
    },
    [temporaryHoursCalendarApi],
  );

  const addClosedDummyEvent = useCallback(
    (start: DateTime, end: DateTime, oderType: LocationHoursOrderType) => {
      const title =
        oderType === 'PICKUP_CLOSED'
          ? i18n.t('LocationHours.eventTitles.pickupClosed')
          : i18n.t('LocationHours.eventTitles.deliveryClosed');

      const dummyEvent = createDummyEvent(start, end, title, oderType, true);

      temporaryHoursCalendarApi?.addEvent(dummyEvent);
      return dummyEvent;
    },
    [temporaryHoursCalendarApi],
  );

  const removeDummyEvent = useCallback(() => {
    const dummyEvent = temporaryHoursCalendarApi?.getEventById(DUMMY_EVENT_ID);
    if (dummyEvent) {
      dummyEvent.remove();
    }
  }, [temporaryHoursCalendarApi]);

  const addTemporaryEvents = useCallback(
    (setHourFormData: SetHoursFormDataProps) => {
      const newTemporaryEvents =
        convertSetTemporaryHoursFormDataToEventSource(setHourFormData);

      const newClosedEvents =
        convertSetClosedDatesFormDataToEventSource(setHourFormData);

      const overlappedTemporaryEvents = filterTemporaryHoursOverlappedEvents(
        setHourFormData,
        temporaryEvents,
      );

      const overlappedClosedEvents = filterClosedDatesOverlappedEvents(
        setHourFormData,
        temporaryEvents,
      );

      // Remove overlapped events from the calendar
      overlappedTemporaryEvents.forEach(event => {
        temporaryHoursCalendarApi?.getEventById(event.id).remove();
      });

      overlappedClosedEvents.forEach(event => {
        temporaryHoursCalendarApi?.getEventById(event.id).remove();
      });

      // Remove existing event if updating
      if (setHourFormData.eventId) {
        temporaryHoursCalendarApi
          ?.getEventById(setHourFormData.eventId)
          .remove();
      }

      // Add merged or new events to the calendar
      temporaryHoursCalendarApi?.addEventSource([
        ...newTemporaryEvents,
        ...newClosedEvents,
      ]);
    },
    [temporaryEvents, temporaryHoursCalendarApi],
  );

  const addClosedEvents = useCallback(
    (setHourFormData: SetHoursFormDataProps) => {
      const newEvents =
        convertSetClosedDatesFormDataToEventSource(setHourFormData);

      const overlappedEvents = filterClosedDatesOverlappedEvents(
        setHourFormData,
        temporaryEvents,
      );

      // Remove overlapped events from the calendar
      overlappedEvents.forEach(event => {
        temporaryHoursCalendarApi?.getEventById(event.id).remove();
      });

      // Remove existing event if updating
      if (setHourFormData.eventId) {
        temporaryHoursCalendarApi
          ?.getEventById(setHourFormData.eventId)
          .remove();
      }

      // Add new events to the calendar
      temporaryHoursCalendarApi?.addEventSource(newEvents);
    },
    [temporaryEvents, temporaryHoursCalendarApi],
  );

  const eventsSet = useCallback((events: EventImpl[]) => {
    setTemporaryEvents(events.map(event => event.toPlainObject()));
  }, []);

  const handleCalendarDateChange = useCallback(
    (calendarApi: CalendarApi, direction: 'prev' | 'today' | 'next') => {
      const currentView = calendarApi.view.type;
      const currentDate = DateTime.fromJSDate(calendarApi.getDate());
      let newDate;

      switch (direction) {
        case 'prev':
          if (currentView === 'timeGridWeek') {
            newDate = currentDate.minus({ days: 7 });
            calendarApi.gotoDate(newDate.toJSDate());
          } else {
            calendarApi.prev();
          }
          break;
        case 'next':
          if (currentView === 'timeGridWeek') {
            newDate = currentDate.plus({ days: 7 });
            calendarApi.gotoDate(newDate.toJSDate());
          } else {
            calendarApi.next();
          }
          break;
        default:
          calendarApi.today();
      }
      setSideCalendarValue(DateTime.fromJSDate(calendarApi.getDate()));
    },
    [],
  );

  const handleCalendarViewChange = useCallback(
    (calendarApi: CalendarApi, view: 'list' | 'week' | 'month' | 'year') => {
      setCalendarView(view);

      switch (view) {
        case 'week':
          calendarApi?.changeView('timeGridWeek');
          break;
        case 'month':
          calendarApi?.changeView('dayGridMonth');
          break;
        case 'year':
          calendarApi?.changeView('multiMonthYear');
          break;
        default:
          calendarApi?.changeView('listWeek');
      }
    },
    [],
  );

  const changeToWeekOfDateView = useCallback(
    (calendarApi: CalendarApi, dateSelect: DateTime) => {
      calendarApi?.gotoDate(dateSelect.toJSDate());
      handleCalendarViewChange(calendarApi, 'week');
    },
    [handleCalendarViewChange],
  );

  const convertEventsToTemporaryHours =
    useCallback((): Partial<StoreOrderingForm> => {
      return eventsToTemporaryHours(temporaryEvents);
    }, [temporaryEvents]);

  const convertEventsToClosedDates =
    useCallback((): Partial<StoreOrderingForm> => {
      return eventsToClosedDates(temporaryEvents);
    }, [temporaryEvents]);

  const removeAllBackgroundEvents = useCallback(() => {
    const allEvents = temporaryHoursCalendarApi.getEvents();
    allEvents.forEach(event => {
      if (['background', 'none'].includes(event.display)) {
        event.remove();
      }
    });
  }, [temporaryHoursCalendarApi]);

  useEffect(() => {
    if (temporaryHoursCalendarApi && !initialLoaded) {
      temporaryHoursCalendarApi.removeAllEvents();
      temporaryHoursCalendarApi.addEventSource(initialEvents);
      setTemporaryEvents(initialEvents); // Set initial events to normalEvents state
      setInitialLoaded(true); // Set initial load flag to true
    }
  }, [initialEvents, temporaryHoursCalendarApi, initialLoaded]);

  // Add background events separately if they are not included in initialEvents
  useEffect(() => {
    if (backgroundEvents && temporaryHoursCalendarApi) {
      removeAllBackgroundEvents();
      temporaryHoursCalendarApi.addEventSource(backgroundEvents);
    }
  }, [backgroundEvents, removeAllBackgroundEvents, temporaryHoursCalendarApi]);

  useEffect(() => {
    return (): void => {
      handleCalendarViewChange(temporaryHoursCalendarApi, 'week');
      setSideCalendarValue(DateTime.now());
    };
  }, [handleCalendarViewChange, temporaryHoursCalendarApi]);

  return {
    temporaryHours: {
      temporaryHoursRef,
      temporaryEvents,
      backgroundEvents,
      calendarView,
      activeStart,
      activeEnd,
      addDummyEvent: addTemporaryDummyEvent,
      removeDummyEvent,
      addEvents: addTemporaryEvents,
      eventsSet,
      handleCalendarDateChange,
      handleCalendarViewChange,
      changeToWeekOfDateView,
      convertEventsToTemporaryHours,
      temporaryHoursCalendarLoaded: initialLoaded,
    },
    closedDates: {
      addDummyEvent: addClosedDummyEvent,
      removeDummyEvent,
      addEvents: addClosedEvents,
      convertEventsToClosedDates,
    },
    sideCalendar: { sideCalendarValue, setSideCalendarValue },
    standardHours: { isShowingNormalHours, setIsShowingNormalHours },
  };
};
